// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { IArticleLocationProcessor, InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { Log } from '@shared/debug/log';
import { Observable, Subject } from 'rxjs';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { ArticleVO } from '@article/model/vo/article.vo';

@Injectable({ providedIn: 'root' })
export class InsertArticleService {

  private _allArticlesLoaded$ = new Subject<void>();

  private _articleInserted$ = new Subject<InsertedArticleResult>();
  private _createProcessor: (articleLocation: ArticleLocationVO, article: ArticleVO, messageType: string) => IArticleLocationProcessor;
  private initialized = false;

  public get allArticlesLoaded$(): Observable<void> {
    return this._allArticlesLoaded$.asObservable();
  }

  public get articleInserted$(): Observable<InsertedArticleResult> {
    return this._articleInserted$.asObservable();
  }

  public markAllArticlesLoaded(): void {
    this._allArticlesLoaded$.next(undefined);
  }

  public async insertArticleByArticleLocation(
    article: ArticleVO,
    articleLocation: ArticleLocationVO,
    messageType = 'insertArticleByLocation'
  ): Promise<InsertedArticleData | null> {
    if (!article || !articleLocation) {
      Log.error('Cannot insert article with given info: ')
      Log.error(`Article: ${JSON.stringify(article)}`)
      Log.error(`ArticleLocation: ${JSON.stringify(articleLocation)}`)
      return;
    }

    let relativeProcessor: IArticleLocationProcessor;

    try {
      relativeProcessor = this.createProcessor(articleLocation, article, messageType);
    } catch (error) {
      const newError = new Error('Processor could not be created: ' + error.toString());
      Log.error(newError);
      this._articleInserted$.next({messageType, error: newError});
      throw newError;
    }

    if (!relativeProcessor) {
      const error = new Error('Processor could not be created, unknown instance.')
      Log.error(error.message);
      this._articleInserted$.next({messageType, error });
      throw error;
    }

    let data: InsertedArticleResult = undefined;
    try {
      const insertedArticleData = await relativeProcessor.process();
      data = { messageType, insertedArticleData };
    } catch (error) {
      data = { messageType, error }
    }

    this._articleInserted$.next(data);

    if (data.error) {
      throw data.error;
    }

    return data.insertedArticleData;
  }

  public initialize(createProcessor: (articleLocation: ArticleLocationVO, article: ArticleVO, messageType: string) => IArticleLocationProcessor) {
    this._createProcessor = createProcessor;
    this.initialized = true;
  }

  private createProcessor(articleLocation: ArticleLocationVO, article: ArticleVO, messageType: string) {
    if (!this.initialized) {
      throw new Error('InsertArticleService not initialized!');
    }
    return this._createProcessor(articleLocation, article, messageType);
  }
}

interface InsertedArticleResult {
  messageType: string,
  insertedArticleData?: InsertedArticleData,
  error?: Error
}
